import { MouseEvent } from 'react'
import IconButton from '@mui/material/IconButton'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import Tooltip from './Tooltip'
import Toast from '../feedback/Toast'
import strings from 'l10n'

type CopyButtonProps = {
  /** Text copied to the clipboard. When empty/null the button renders nothing. */
  value?: string | null
  /** Tooltip on hover, also used as the accessible label (default: "Copy"). */
  tooltip?: string
  /** Toast message shown after copying (default: "Copied"). */
  copiedMessage?: string
  /** Consumer hook to position/style the button (e.g. reveal-on-hover). */
  className?: string
}

/**
 * Small reusable icon button that copies `value` to the clipboard and shows a confirmation toast.
 * Layout (e.g. reveal-on-hover) is left to the consumer via `className`.
 */
const CopyButton = ({ value, tooltip = strings.COPY, copiedMessage = strings.COPIED, className }: CopyButtonProps) => {
  if (!value) return null

  const handleCopy = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    try {
      await navigator.clipboard.writeText(value)
      Toast({ type: 'success', subTitle: copiedMessage })
    } catch {
      Toast({ type: 'error', subTitle: strings.COPY_FAILED })
    }
  }

  return (
    <Tooltip title={tooltip} placement="top" arrow margin="0">
      <IconButton size="small" className={className} aria-label={tooltip} onClick={handleCopy}>
        <ContentCopyOutlinedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  )
}

export default CopyButton
